import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';

import { db } from '../../config/firebaseConfig.js';
import PageContainer from '../PageUtil/PageContainer.jsx';

const normalizeId = (value) => (typeof value === 'string' ? value.trim() : '');

const DebugProjectVisibility = () => {
  const { userId: routeUserId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialUserId = normalizeId(routeUserId || searchParams.get('userId'));
  const initialProjectId = normalizeId(searchParams.get('projectId'));

  const [userId, setUserId] = useState(initialUserId);
  const [projectId, setProjectId] = useState(initialProjectId);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const projectDocRef = useMemo(() => {
    const normalizedUserId = normalizeId(userId);
    const normalizedProjectId = normalizeId(projectId);

    if (!normalizedUserId || !normalizedProjectId) {
      return null;
    }

    return doc(db, 'users', normalizedUserId, 'projects', normalizedProjectId);
  }, [userId, projectId]);

  const handleInspect = async (event) => {
    event.preventDefault();

    const normalizedUserId = normalizeId(userId);
    const normalizedProjectId = normalizeId(projectId);

    setError(null);
    setResult(null);

    if (!normalizedUserId || !normalizedProjectId) {
      setError('Enter both a user ID and a project ID.');
      return;
    }

    setLoading(true);

    try {
      const snapshot = await getDoc(projectDocRef);

      if (!snapshot.exists()) {
        setResult({
          status: 'missing',
          message: 'Project document does not exist.',
        });
        return;
      }

      const data = snapshot.data();
      setResult({
        status: 'readable',
        message: 'Project document is readable with the current rules.',
        data: {
          id: snapshot.id,
          title: data.title || null,
          isPublished: data.isPublished === true,
          publicRefsCount: data.publicRefsCount || 0,
          imageCount: Array.isArray(data.images) ? data.images.length : 0,
        },
      });
    } catch (fetchError) {
      setResult({
        status: 'blocked',
        message: fetchError?.message || 'Read blocked by Firestore rules.',
      });
    } finally {
      setLoading(false);
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        if (normalizedUserId) {
          next.set('userId', normalizedUserId);
        }
        if (normalizedProjectId) {
          next.set('projectId', normalizedProjectId);
        }
        return next;
      });
    }
  };

  return (
    <PageContainer error={error} setError={setError}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 col-xl-7">
            <div className="card shadow-sm border-0">
              <div className="card-body p-4 p-md-5">
                <h1 className="h3 fw-bold mb-2">Project Visibility Debug</h1>
                <p className="text-muted mb-4">
                  Use this page without logging in to test whether a user-scoped project document is publicly readable.
                </p>

                <form onSubmit={handleInspect} className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="debug-project-user-id">User ID</label>
                    <input
                      id="debug-project-user-id"
                      className="form-control"
                      value={userId}
                      onChange={(event) => setUserId(event.target.value)}
                      placeholder="Firebase user uid"
                      autoComplete="off"
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="debug-project-id">Project ID</label>
                    <input
                      id="debug-project-id"
                      className="form-control"
                      value={projectId}
                      onChange={(event) => setProjectId(event.target.value)}
                      placeholder="Firestore project document id"
                      autoComplete="off"
                    />
                  </div>

                  <div className="col-12 d-flex gap-2 align-items-center">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Checking...' : 'Check Visibility'}
                    </button>
                    <span className="text-muted small">
                      Tests the path: users/{'{userId}'}/projects/{'{projectId}'}
                    </span>
                  </div>
                </form>

                {result ? (
                  <div className={`alert mt-4 mb-0 alert-${result.status === 'readable' ? 'success' : result.status === 'blocked' ? 'danger' : 'warning'}`}>
                    <div className="fw-semibold mb-1">{result.status.toUpperCase()}</div>
                    <div className="mb-2">{result.message}</div>
                    {result.data ? (
                      <pre className="mb-0 small bg-light p-3 rounded border overflow-auto">{JSON.stringify(result.data, null, 2)}</pre>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default DebugProjectVisibility;
